import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { forgotPassword, login as loginService, resetPassword } from '../services/authService';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import NotificationSnackbar from './ui/NotificationSnackbar';
import '../assets/styles/Login.scss';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotStep, setForgotStep] = useState('email');
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [forgotBusy, setForgotBusy] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  const { login } = useAuth();
  const { reloadSettings } = useSettings();
  const navigate = useNavigate();
  const notify = (message, severity = 'success') => setNotification({ open: true, message, severity });

  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleLogin = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const data = await loginService(email.trim(), password);
      if (!data?.accessToken || !data?.user) throw new Error('The server returned an invalid login response');
      login(data.user, data.accessToken);
      reloadSettings();
      if (rememberMe) localStorage.setItem('rememberedEmail', email.trim());
      else localStorage.removeItem('rememberedEmail');
      notify('Login successful');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      notify(err?.response?.data?.error || err?.message || 'Unable to log in. Check your email and password.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const openForgot = () => {
    setForgotEmail(email.trim());
    setResetOtp('');
    setNewPassword('');
    setForgotStep('email');
    setForgotOpen(true);
  };

  const sendResetOtp = async () => {
    if (!forgotEmail.trim()) return notify('Enter your email first.', 'warning');
    setForgotBusy(true);
    try {
      const result = await forgotPassword(forgotEmail.trim());
      setForgotStep('reset');
      notify(result?.message || 'Reset OTP sent to your email.');
    } catch (err) {
      notify(err?.response?.data?.error || err?.message || 'Could not send the reset OTP.', 'error');
    } finally {
      setForgotBusy(false);
    }
  };

  const completeReset = async () => {
    if (!resetOtp.trim() || newPassword.length < 8) {
      return notify('Enter the 6-digit OTP and a password of at least 8 characters.', 'warning');
    }
    setForgotBusy(true);
    try {
      const result = await resetPassword(resetOtp.trim(), newPassword, forgotEmail.trim());
      notify(result?.message || 'Password reset successfully.');
      setEmail(forgotEmail.trim());
      setPassword('');
      setForgotOpen(false);
    } catch (err) {
      notify(err?.response?.data?.error || err?.message || 'Could not reset the password.', 'error');
    } finally {
      setForgotBusy(false);
    }
  };

  return (
    <main className="login">
      <section className="login-brand-panel">
        <div className="login-brand-content">
          <div className="login-brand-mark"><RestaurantMenuIcon /></div>
          <p className="login-eyebrow">JDH Caterers</p>
          <h1>Hospitality operations, organised.</h1>
          <p>Manage website orders, kitchen production, delivery, invoicing, products and customer communication from one place.</p>
          <div className="login-brand-points">
            <span>Live website order visibility</span>
            <span>KOT and delivery workflow</span>
            <span>Invoices and payment status</span>
          </div>
        </div>
      </section>

      <section className="login-form-panel">
        <div className="login-wrapper">
          <div className="login-mobile-brand">
            <div className="login-brand-mark"><RestaurantMenuIcon /></div>
            <span>JDH Caterers</span>
          </div>
          <p className="login-eyebrow">Fusion Box Dashboard</p>
          <h2>Welcome back</h2>
          <p className="login-subtitle">Sign in using your JDH dashboard account.</p>

          <form className="login-form" onSubmit={handleLogin}>
            <TextField
              label="Email address"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              fullWidth
              required
              autoComplete="email"
              InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon /></InputAdornment> }}
              margin="normal"
            />
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              fullWidth
              required
              autoComplete="current-password"
              InputProps={{
                startAdornment: <InputAdornment position="start"><LockIcon /></InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" aria-label={showPassword ? 'Hide password' : 'Show password'} onClick={() => setShowPassword((value) => !value)}>
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              margin="normal"
            />

            <Box className="login-form-options">
              <FormControlLabel
                control={<Checkbox checked={rememberMe} onChange={(event) => setRememberMe(event.target.checked)} />}
                label="Remember me"
              />
              <Button type="button" variant="text" onClick={openForgot}>Forgot password?</Button>
            </Box>

            <Button type="submit" variant="contained" fullWidth disabled={submitting}>
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
          <p className="login-support">Authorised JDH team members only.</p>
        </div>
      </section>

      <Dialog open={forgotOpen} onClose={() => !forgotBusy && setForgotOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{forgotStep === 'email' ? 'Forgot password' : 'Enter reset OTP'}</DialogTitle>
        <DialogContent sx={{ pt: '10px !important' }}>
          {forgotStep === 'email' ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>We’ll email a 6-digit OTP to reset your password.</Typography>
              <TextField label="Dashboard email" type="email" value={forgotEmail} onChange={(event) => setForgotEmail(event.target.value)} fullWidth autoFocus />
            </>
          ) : (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">OTP sent to {forgotEmail}. It expires in 15 minutes.</Typography>
              <TextField label="6-digit OTP" value={resetOtp} onChange={(event) => setResetOtp(event.target.value.replace(/\D/g, '').slice(0, 6))} fullWidth />
              <TextField
                label="New password"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                helperText="Minimum 8 characters"
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton edge="end" onClick={() => setShowNewPassword((value) => !value)}>
                        {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button variant="text" onClick={sendResetOtp} disabled={forgotBusy}>Resend OTP</Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForgotOpen(false)} disabled={forgotBusy}>Cancel</Button>
          <Button variant="contained" onClick={forgotStep === 'email' ? sendResetOtp : completeReset} disabled={forgotBusy}>
            {forgotBusy ? 'Please wait…' : forgotStep === 'email' ? 'Send OTP' : 'Reset password'}
          </Button>
        </DialogActions>
      </Dialog>

      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification((value) => ({ ...value, open: false }))}
      />
    </main>
  );
};

export default Login;
